import {TOWN_REPORTS} from '../data/townReportNavigation';
// src/components/Town/townBudget.js

import React, { useEffect, useMemo, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase.js";

import BudgetCard from "./budgetCard.js";
import TownBudgetOverview from "./TownBudgetOverview";
import townBanner from "../images/town-hall-budget-banner.png";
import riverfrontBanner from "../images/riverfront-banner.png";
import ciderMillBanner from "../images/cider-mill-banner.png";
import libraryBanner from "../images/library-banner.png";
import {ReportPageHeader, ReportChapterHeader} from "../../../shared/ui/layout/ReportPage";
import {
  SummaryOfRevenues,
  SummaryOfExpenditures,
  SummaryOfProjections,
  SummaryOfRevenuesAndExpenditures,
  ProgramLicensesAndPermits,
  ProgramIntergovernmental,
  ProgramChargesForServices,
  ProgramOtherRevenues,
  ProgramTransfersIn,
  ProgramTown,
  ProgramDebtServiceAndTransfersOut,
  ProgramTaxRevenues,
  ProgramAdministrativeServices,
} from "./index.js";

import { useAuth } from "../../../app/context/AuthContext.jsx";
import {
  ArrowUturnLeftIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { saveBudgetReportEdits } from "../utils/saveBudgetReportEdits.js";
import { getAuth } from "firebase/auth";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton";

function CollapsibleSection({ title, children, defaultCollapsed = true }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const controlsId = `town-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
      <HeaderToggleButton reportNavigation title={title} controlsId={controlsId} ariaLabel={`Toggle ${title}`}
        isOpen={!collapsed} onToggle={() => setCollapsed(prev => !prev)} />

      {!collapsed && <div id={controlsId} className="bg-white p-4">{children}</div>}
    </div>
  );
}

function extractYearKeys(docData) {
  if (!docData || typeof docData !== "object") return [];
  return Object.keys(docData)
    .filter((k) => /^\d{4}$/.test(k))
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && n >= 2000 && n <= 2100)
    .sort((a, b) => a - b);
}

export function TownBudget() {
  const [isEditing, setIsEditing] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  // Year options are still loaded here, but we do NOT render a global dropdown anymore.
  const [availableYears, setAvailableYears] = useState([]);
  const [numericYears, setNumericYears] = useState([]); // used by sections that still expect numericYears
  const [isLoadingYears, setIsLoadingYears] = useState(true);
  const [projectionYears, setProjectionYears] = useState([]);

  const [revenuesData, setRevenuesData] = useState({});
  const [expendituresData, setExpendituresData] = useState({});
  const [fundBalanceData, setFundBalanceData] = useState({});
  const [townProgramsData, setTownProgramsData] = useState({});

  const { currentUser } = useAuth();
  const userCanEdit =
    currentUser?.roles?.includes("admin") ||
    currentUser?.roles?.includes("budget");

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    // currently unused, but keeping in case you wire something here later
  }, []);

  // Load available fiscal years from Firestore (probe one or two known docs).
  useEffect(() => {
    let isMounted = true;

    async function loadYears() {
      setIsLoadingYears(true);

      try {
        const probeDocIds = ["revenues_tax_revenues", "expenditures_town"];

        let years = [];

        for (const probeId of probeDocIds) {
          const snap = await getDoc(doc(db, "townGovernment", probeId));
          if (!snap.exists()) continue;

          const data = snap.data();
          years = extractYearKeys(data);

          if (years.length) break;
        }

        if (!isMounted) return;

        setAvailableYears(years);

        // Keep numericYears as a default for components that still rely on it.
        const latest = years.length ? years[years.length - 1] : null;
        setNumericYears(latest ? [latest] : []);

        // Load projection year keys (these may not match revenue/expenditure year keys)
        const projectionProbeDocIds = ["Projections", "projections"];

        let pYears = [];

        for (const probeId of projectionProbeDocIds) {
          const snap = await getDoc(doc(db, "townGovernment", probeId));
          if (!snap.exists()) continue;

          const data = snap.data();
          pYears = extractYearKeys(data);

          if (pYears.length) break;
        }

        if (isMounted) {
          setProjectionYears(pYears);
        }
      } catch (err) {
        console.error("Failed to load Town budget year options:", err);
        if (!isMounted) return;
        setAvailableYears([]);
        setNumericYears([]);
        setProjectionYears([]);
      } finally {
        if (isMounted) setIsLoadingYears(false);
      }
    }

    loadYears();
    return () => {
      isMounted = false;
    };
  }, []);

  const currentYear = numericYears[0] || null;

  const handleCancelAllChanges = () => {
    setRevenuesData({});
    setExpendituresData({});
    setFundBalanceData({});
    setTownProgramsData({});
    setIsEditing(false);
    setSaveMessage("Changes canceled.");
    setTimeout(() => setSaveMessage(""), 2500);
  };

  const handleSaveAllChanges = async () => {
    // If your save util needs a fiscal year, keep passing something stable:
    if (!currentYear) return;

    const result = await saveBudgetReportEdits({
      fiscalYear: currentYear,
      revenuesData,
      expendituresData,
      fundBalanceData,
      townProgramsData,
    });

    if (result.success) {
      setIsEditing(false);
      setSaveMessage("Changes saved.");
    } else {
      setSaveMessage("Failed to save changes.");
    }

    setTimeout(() => setSaveMessage(""), 2500);
  };

  // Only pass overrides when we actually have them, otherwise let child components fetch normally.
  const revenuesDocOverride = useMemo(() => {
    if (!currentYear) return undefined;
    const d = revenuesData?.[currentYear]?.__doc;
    return d && Object.keys(d).length ? d : undefined;
  }, [revenuesData, currentYear]);

  const expendituresOverride = useMemo(() => {
    if (!currentYear) return undefined;
    const d = expendituresData?.[currentYear]?.__doc;
    return d && Object.keys(d).length ? d : undefined;
  }, [expendituresData, currentYear]);

  const fundBalanceOverride = useMemo(() => {
    if (!currentYear) return undefined;
    const d = fundBalanceData?.[currentYear]?.__doc;
    return d && Object.keys(d).length ? d : undefined;
  }, [fundBalanceData, currentYear]);

  if (isLoadingYears) {
    return (
      <div className="min-h-screen bg-base-200 text-base-content flex items-center justify-center">
        <div className="text-sm text-gray-600">Loading fiscal years…</div>
      </div>
    );
  }

  if (!availableYears.length) {
    return (
      <div className="min-h-screen bg-base-200 text-base-content flex items-center justify-center">
        <div className="text-sm text-gray-700 bg-white border border-gray-300 rounded-md px-4 py-3">
          No fiscal years found in Firestore (townGovernment). Check that at
          least one of these docs exists and contains year keys like "2024":
          <div className="mt-2 text-xs text-gray-600">
            revenues_tax_revenues, expenditures_town
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 text-base-content flex flex-col">
      <div className="flex flex-1 w-full justify-center">
        <div className="w-full max-w-screen-2xl">
          <BudgetCard>
            <div className="space-y-8 sm:space-y-10">
              <div className="space-y-3 sm:space-y-4">
              <img src={townBanner} alt="Illustration of Glastonbury Town Hall with its red brick facade, white-trimmed windows, and surrounding trees" className="h-auto w-full object-contain" />
              <ReportPageHeader editorial title="Town Budget" headingAs="h2"
                actions={
                userCanEdit && (
                  <div className="flex items-center gap-3">
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1 text-xs text-white bg-blue-600 hover:bg-blue-700 font-medium px-3 py-1 rounded-md shadow-sm"
                      >
                        <PencilSquareIcon className="w-4 h-4 text-white" />
                        Edit
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleSaveAllChanges}
                          className="flex items-center gap-1 text-xs text-white bg-green-600 hover:bg-green-700 font-medium px-3 py-1 rounded-md shadow-sm"
                        >
                          💾 Save
                        </button>
                        <button
                          onClick={handleCancelAllChanges}
                          className="flex items-center gap-1 text-xs text-white bg-red-600 hover:bg-red-700 font-medium px-3 py-1 rounded-md shadow-sm"
                        >
                          <ArrowUturnLeftIcon className="w-4 h-4 text-white" />
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                )
              } />
              </div>
              <p className="text-base leading-7 text-slate-600">
                Explore how Glastonbury funds town services and plans for the years ahead. Open any report below to see the details.
              </p>

              <TownBudgetOverview />

              {saveMessage && (
                <div className="text-sm text-green-700 font-semibold bg-green-100 border border-green-300 rounded-md py-2 px-4 mb-2">
                  {saveMessage}
                </div>
              )}

              <ReportChapterHeader title="Budget at a glance" tone="lavender"
                image={riverfrontBanner} imageAlt="Watercolor illustration of Glastonbury’s riverfront park, pavilion, and dock"
                description="Start with the overall picture: revenues, spending, and the town’s projected fund balance. Each report has its own fiscal year controls." />

              {/* Combined Summary: year dropdown now lives INSIDE the component and only affects it */}
              <SummaryOfRevenuesAndExpenditures
                availableYears={availableYears}
              />

              <SummaryOfRevenues
                availableYears={availableYears}
                isEditing={isEditing}
                setData={setRevenuesData}
                dataOverride={revenuesDocOverride}
              />

              <SummaryOfExpenditures
                availableYears={availableYears}
                isEditing={isEditing}
                setData={setExpendituresData}
                dataOverride={expendituresOverride}
              />

              <SummaryOfProjections
                availableYears={
                  projectionYears.length ? projectionYears : availableYears
                }
                numericYears={numericYears}
                isEditing={isEditing}
                setData={setFundBalanceData}
                dataOverride={fundBalanceOverride}
              />

              {/* ----------------------------------------- */}
              {/* PROGRAMS (Town Government)                */}
              {/* ----------------------------------------- */}
              <div className="mt-4">
                <ReportChapterHeader title="Revenue programs" tone="orange"
                  image={ciderMillBanner} imageAlt="Watercolor illustration of the Old Cider Mill with pumpkins and autumn trees"
                  description="Where the funding comes from, including property taxes, permits, state and federal support, and service fees." />

                <div className="mt-5 space-y-4">
                  <CollapsibleSection
                    title={TOWN_REPORTS[4].title}
                    defaultCollapsed={true}
                  >
                    <ProgramTaxRevenues
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title={TOWN_REPORTS[5].title}
                    defaultCollapsed={true}
                  >
                    <ProgramLicensesAndPermits
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title={TOWN_REPORTS[6].title}
                    defaultCollapsed={true}
                  >
                    <ProgramIntergovernmental
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title={TOWN_REPORTS[7].title}
                    defaultCollapsed={true}
                  >
                    <ProgramChargesForServices
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title={TOWN_REPORTS[8].title}
                    defaultCollapsed={true}
                  >
                    <ProgramOtherRevenues
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title={TOWN_REPORTS[9].title}
                    defaultCollapsed={true}
                  >
                    <ProgramTransfersIn
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>
                </div>
              </div>

              {/* ----------------------------------------- */}
              {/* EXPENDITURE PROGRAMS (Town Government)     */}
              {/* ----------------------------------------- */}
              <div className="mt-8">
                <ReportChapterHeader title="Expenditure programs" tone="green"
                  image={libraryBanner} imageAlt="Watercolor illustration of children and families enjoying the library’s reading nooks"
                  description="How funds support town departments, debt service, and transfers to other funds." />

                <div className="mt-5 space-y-4">
                  <CollapsibleSection title={TOWN_REPORTS[10].title} defaultCollapsed={true}>
                    <ProgramTown
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title={TOWN_REPORTS[11].title}
                    defaultCollapsed={true}
                  >
                    <ProgramDebtServiceAndTransfersOut
                      isEditing={isEditing}
                      setTownProgramsData={setTownProgramsData}
                      collectionName="townGovernment"
                    />
                  </CollapsibleSection>
                </div>
              </div>
            </div>
          </BudgetCard>
        </div>
      </div>
    </div>
  );
}
